import React, { useState, useEffect, useMemo, useRef } from "react";
import CustomInput from "../../../common/CustomInput";
import Button from "../../../common/Button";
import { useDispatch, useSelector } from "react-redux";
import { getMedicationReview, setSelectedMedicalSummary } from "../../../../redux/modules/medical-summary/actions";
import ActionsMenu from "./ActionsMenu";
import ConfirmationModalNew from "../../../common/ConfirmationModalNew";
import ResumeClinicalServiceModal, { evaluateResumeDecision } from "../../../common/ResumeClinicalServiceModal";
import { formatDateToDD_MMM_YYYY, capitalizeFirstLetter } from "../../../../helpers/functions";
import { medicationReviewTypes } from "../../text/data";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import FormIcons from "./FormIcons";
import { updateMinorAilmentStatus } from "../../../../redux/modules/clinical-services/actions";
import { displayToast } from "../../../../redux/modules/toast/actions";

const normalizeStatus = (status) => String(status || "").toLowerCase();

const isRunningService = (service) => {
  const status = normalizeStatus(service?.status);
  return status === 'in-progress' || status === 'inprogress' || status === 'in_progress' || status === 'in progress';
};

const computeRangeForOption = (value) => {
  const now = new Date();
  let from = null;

  if (value === "last4Weeks") {
    from = new Date(now);
    from.setDate(now.getDate() - 28);
  } else if (value === "last3Months") {
    from = new Date(now);
    from.setMonth(now.getMonth() - 3);
  } else if (value === "last6Months") {
    from = new Date(now);
    from.setMonth(now.getMonth() - 6);
  } else if (value === "last12Months") {
    from = new Date(now);
    from.setFullYear(now.getFullYear() - 1);
  }

  return { from, to: now };
};

const getMedicationReviewRowId = (item, index, reviewType) =>
  item?.id ?? `${item?.medicationReviewTypeCode || 'type'}-${item?.date || 'nodate'}-${reviewType}-${index}`;

const getReviewTitleByCode = (code, medicationReviewTypeOptions) => {
  if (!code) return "N/A";
  if (code === 'AnnualMedicationReview') return 'Annual Medication Review';
  if (code === 'FOLLOWUP' || code === 'FollowUp' || code === 'followup') return 'Follow Up';

  const option = medicationReviewTypeOptions.find((item) => item.value === code);
  return option ? option.label : code;
};

const formatStatus = (statusOrRecord) => {
  if (!statusOrRecord) return "N/A";

  const status = typeof statusOrRecord === "string" ? statusOrRecord : statusOrRecord?.status;
  const normalizedStatus = normalizeStatus(status);

  if (!normalizedStatus) return "N/A";
  if (
    normalizedStatus === "complete_no_referral" ||
    normalizedStatus === "completed_no_referral" ||
    normalizedStatus === "complete & no referral" ||
    normalizedStatus === "completed & no referral"
  ) return "Complete & No Referral";
  if (
    normalizedStatus === "complete_with_referral" ||
    normalizedStatus === "completed_with_referral" ||
    normalizedStatus === "complete with referral" ||
    normalizedStatus === "completed with referral"
  ) return "Complete with Referral";
  if (
    normalizedStatus === "end_service_referral" ||
    normalizedStatus === "end_consult_referral" ||
    normalizedStatus === "end service & referral" ||
    normalizedStatus === "end consult & referral"
  ) return "End Service & Referral";
  if (normalizedStatus === "deleted") return "Deleted Service";
  if (normalizedStatus === "deleted_service") return "Deleted Service";
  if (normalizedStatus === "cancelled" || normalizedStatus === "cancelled_service") return "Cancelled Service";
  if (normalizedStatus === "in-progress" || normalizedStatus === "in_progress" || normalizedStatus === "inprogress") return "In-Progress";
  if (
    normalizedStatus === "incomplete_technical_issue" ||
    normalizedStatus === "incomplete_power_outage" ||
    normalizedStatus === "power_outage" ||
    normalizedStatus === "technical_issue"
  ) return "Incomplete - Technical Issue";
  if (normalizedStatus === "completed") return "Complete";

  return String(normalizedStatus)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getRecordKey = (record) => {
  if (!record) return "";
  if (record.id) return `id:${record.id}`;
  try {
    return `hash:${JSON.stringify(record)}`;
  } catch (error) {
    return "";
  }
};

const MedicationReviewRow = ({
  item,
  rowId,
  selectedRowId,
  currentTabIndex,
  onSelect,
  onEdit,
  onDelete,
  getMedicationReviewTitle,
  formatStatus,
}) => {
  return (
    <tr key={rowId} className={`transition-colors duration-300 group hover:bg-blue-100 ${selectedRowId === rowId ? "bg-blue-100" : ""}`}>
      <td className="py-4 pl-6 pr-12 text-base font-semibold text-gray-800 whitespace-pre-wrap">
        <div className="flex items-center gap-3">
          <div className="flex items-center">
            <input
              id={`medicationReviewRow-${rowId}`}
              name="medicationReviewRow"
              type="radio"
              value={rowId}
              checked={selectedRowId === rowId}
              onChange={() => onSelect(item, rowId)}
              className="w-5 h-5 cursor-pointer text-teal-50 focus:ring-transparent"
            />
            <label
              htmlFor={`medicationReviewRow-${rowId}`}
              className="block ml-3 text-teal-600 text-body-md cursor-pointer"
            >
              <span className="font-semibold text-gray-800 leading-tight text-md-body">
                {getMedicationReviewTitle(item.medicationReviewTypeCode)}
              </span>
            </label>
          </div>
        </div>
      </td>
      <td className="px-6 py-3 gap-2 text-base font-semibold text-gray-700 whitespace-pre-wrap">
        {item.date ? formatDateToDD_MMM_YYYY(new Date(item.date)) : "N/A"}
      </td>
      <td className="px-6 py-4 gap-2 text-base font-normal text-gray-700 whitespace-pre-wrap">Vesna Spadafora</td>
      <td className="px-6 py-4 gap-2 text-base font-normal text-gray-700 whitespace-pre-wrap">{formatStatus(item)}</td>
      <td className="px-6 py-4 gap-2">
        <div className="flex gap-2">
          <FormIcons selected={selectedRowId === rowId} medicalSummaryObject={item} currentTabIndex={currentTabIndex} rowId={rowId} />
        </div>
      </td>
      <td className="px-6 py-4 text-center w-[10%] text-gray-800 whitespace-nowrap">
        <ActionsMenu onEdit={() => onEdit(item, rowId)} onDelete={() => onDelete(item, rowId)} />
      </td>
    </tr>
  );
};

const MedicationReview = ({ patient, currentTabIndex = 0 }) => {
  const dispatch = useDispatch();
  const { id: patientId, summaryId, type } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { medicationReviewResponse, loading } = useSelector(
    (state) => state.medicalSummary
  );
  const { clinicalServicesSmall } = useSelector(
    (state) => state.clinicalService
  );

  const [medicationReviewTypeCode, setMedicationReviewType] = useState("All");
  const [date, setDateRange] = useState("All");
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [selectedSummary, setSelectedSummary] = useState(null);
  const [optimisticStatusByKey, setOptimisticStatusByKey] = useState({});
  const [deleteModalState, setDeleteModalState] = useState({ open: false, record: null });
  const [resumeModalState, setResumeModalState] = useState({ open: false, record: null });
  const lastProcessedUrlRef = useRef(null);

  const [initialLoaded, setInitialLoaded] = useState(false);

  useEffect(() => {
    if (initialLoaded) return;
    if (!patient?.id) return;
    dispatch(
      getMedicationReview(patient.id, {
        medicationReviewTypeCode: medicationReviewTypeCode === "All" ? null : medicationReviewTypeCode,
        // Request unfiltered by date from server and apply date-range filtering client-side
        date: null,
      })
    );
    setInitialLoaded(true);
  }, [initialLoaded, dispatch, patient?.id, medicationReviewTypeCode]);

  useEffect(() => {
    if (summaryId) {
      let currentSummary = medicationReviewResponse.filter(
        (x) => x.id === summaryId
      );
      setSelectedSummary(currentSummary.length > 0 ? currentSummary[0] : null);
    } else {
      // If no summaryId, clear the selected summary
      setSelectedSummary(null);
    }
  }, [medicationReviewResponse, summaryId]);

  // Restore selected row from URL parameters on initial load
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const urlSelectedRowId = urlParams.get('selectedRowId');
    const currentUrlKey = location.search;
    
    // Only process if URL has changed and we have data
    if (urlSelectedRowId && medicationReviewResponse.length > 0 && lastProcessedUrlRef.current !== currentUrlKey) {
      // Find the record that matches the selectedRowId
      const selectedRecord = medicationReviewResponse.find(item => {
        const itemId = item?.id ?? `${item?.medicationReviewTypeCode || 'type'}-${item?.date || 'nodate'}-${item.type === 'followup' ? 'followup' : 'annual'}-${medicationReviewResponse.indexOf(item)}`;
        return itemId === urlSelectedRowId;
      });
      
      if (selectedRecord) {
        setSelectedRowId(urlSelectedRowId);
        setSelectedSummary(selectedRecord);
        dispatch(setSelectedMedicalSummary(selectedRecord));
        lastProcessedUrlRef.current = currentUrlKey;
      }
    } else if (!urlSelectedRowId) {
      // Clear the ref when URL doesn't have selectedRowId
      lastProcessedUrlRef.current = null;
    }
  }, [medicationReviewResponse, location.search, dispatch]);

  const getMedicationReviewData = () => {
    if (patient?.id) {
      dispatch(
        getMedicationReview(patient.id, {
          medicationReviewTypeCode:
            medicationReviewTypeCode === "All"
              ? null
              : medicationReviewTypeCode,
          // Request unfiltered by date from server and apply date-range filtering client-side
          date: null,
        })
      );
    }
  };

  // Extract distinct medicationReviewTypeCode from clinicalServicesSmall.medicationReviews
  // Only include items with status "refer"
  const getDistinctMedicationReviewCodes = () => {
    if (
      !clinicalServicesSmall?.medicationReviews ||
      !Array.isArray(clinicalServicesSmall.medicationReviews)
    ) {
      return [];
    }

    // Filter items with status "refer" and extract distinct codes
    const distinctCodes = [
      ...new Set(
        clinicalServicesSmall.medicationReviews
          .filter((item) => item.status === "refer") // Only include items with status "refer"
          .map((item) => item.medicationReviewTypeCode)
      ),
    ].filter((code) => code); // Remove any null/undefined values

    return distinctCodes;
  };

  const distinctMedicationReviewCodes = useMemo(
    () => getDistinctMedicationReviewCodes(),
    [clinicalServicesSmall?.medicationReviews]
  );

  // Build dynamic options for Minor Ailment Type from imported minorAilments
  // Filter based on distinct codes from clinicalServicesSmall
  const medicationReviewTypeOptions = useMemo(() => ([
    { value: "All", label: "All Reviews" },
    ...(Array.isArray(medicationReviewTypes)
      ? medicationReviewTypes
        .filter((item) => {
          if (item.disable === true) return false;

          if (distinctMedicationReviewCodes.length === 0) return true;

          return distinctMedicationReviewCodes.includes(item.code);
        })
        .map((item) => ({
          value: item.code,
          label: item.title,
        }))
      : []),
  ]), [distinctMedicationReviewCodes]);

  const assessmentTypeOptions = [
    { value: "All", label: "All" },
    { value: "Follow Up", label: "Follow Up" },
    { value: "Initial", label: "Initial" },
  ];

  const dateRangeOptions = [
    { value: "All", label: "All" },
    { value: "last4Weeks", label: "Last 4 weeks" },
    { value: "last3Months", label: "Last 3 months" },
    { value: "last6Months", label: "Last 6 months" },
    { value: "last12Months", label: "Last 12 months" },
    { value: "custom", label: "Custom Range", disabled: true },
  ];

  const filteredAssessmentTypeOptions = assessmentTypeOptions;
  const filteredDateRangeOptions = dateRangeOptions;

  const getMedicationReviewTitleByCode = (code) => getReviewTitleByCode(code, medicationReviewTypeOptions);

  // Apply client-side filters (type + date range) in a compact helper
  const applyFilters = (items = []) => {
    const list = Array.isArray(items) ? items : [];
    const byType = medicationReviewTypeCode === "All" ? list : list.filter(i => i.medicationReviewTypeCode === medicationReviewTypeCode);

    if (!date || date === "All") return byType;

    const { from, to } = computeRangeForOption(date);
    if (!from || !to) return byType;

    return byType.filter(i => {
      if (!i?.date) return false;
      const d = new Date(i.date);
      return d >= from && d <= to;
    });
  };

  const medicationReviewsWithOptimisticStatus = useMemo(() => {
    const source = Array.isArray(medicationReviewResponse) ? medicationReviewResponse : [];

    return source.map((record) => {
      const key = getRecordKey(record);
      const optimisticStatus = key ? optimisticStatusByKey[key] : null;
      if (!optimisticStatus) return record;
      return { ...record, status: optimisticStatus };
    });
  }, [medicationReviewResponse, optimisticStatusByKey]);

  const filteredMedicationReviews = useMemo(
    () => applyFilters(medicationReviewsWithOptimisticStatus),
    [medicationReviewsWithOptimisticStatus, medicationReviewTypeCode, date]
  );

  const followUpReviews = useMemo(
    () => filteredMedicationReviews.filter((item) => item.type === 'followup' || item.medicationReviewTypeCode === 'FOLLOWUP'),
    [filteredMedicationReviews]
  );

  const annualReviews = useMemo(
    () => filteredMedicationReviews.filter((item) => item.medicationReviewTypeCode === 'AnnualMedicationReview'),
    [filteredMedicationReviews]
  );

  const getCurrentRunningService = (small) => {
    const runningMR = (small?.medicationReviews || []).find(isRunningService);
    const runningMA = (small?.minorAilments || []).find(isRunningService);
    return runningMR || runningMA || null;
  };

  const handleResumeNavigate = (record) => {
    if (!patient?.id) return;
    const path = record?.id
      ? `/patient/${patient.id}/clinical-services/${record.id}`
      : `/patient/${patient.id}/clinical-services/`;
    navigate(path, { state: { fromSummary: true, summaryRecord: record } });
  };

  const setOptimisticStatus = (record, nextStatus) => {
    const key = getRecordKey(record);
    if (!key) return;
    setOptimisticStatusByKey((prev) => ({ ...prev, [key]: nextStatus }));
  };

  const openDeleteConfirmForRecord = (record, rowId) => {
    setSelectedRowId(rowId);
    setSelectedSummary(record);
    dispatch(setSelectedMedicalSummary(record));
    setDeleteModalState({ open: true, record });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModalState.record) return;

    // Optimistic soft-delete: keep row, only change status in UI.
    setOptimisticStatus(deleteModalState.record, "deleted_service");

    const activePatientId = patient?.id || patientId;

    try {
      if (deleteModalState.record?.id) {
        await dispatch(updateMinorAilmentStatus(deleteModalState.record.id, "deleted_service"));
      }
    } catch (e) {
      // ignore
    } finally {
      if (activePatientId) {
        await dispatch(
          getMedicationReview(activePatientId, {
            medicationReviewTypeCode: null,
            date: null,
          })
        );
      }
    }

    setDeleteModalState({ open: false, record: null });
  };

  const cancelRunningServiceStatus = async (runningService) => {
    if (!runningService) return;

    setOptimisticStatus(runningService, "cancelled_service");

    const activePatientId = patient?.id || patientId;

    try {
      if (runningService?.id) {
        await dispatch(updateMinorAilmentStatus(runningService.id, "cancelled_service"));
      }
    } finally {
      if (activePatientId) {
        await dispatch(
          getMedicationReview(activePatientId, {
            medicationReviewTypeCode: null,
            date: null,
          })
        );
      }
    }
  };

  const handleStartNewFromResume = async () => {
    const runningService = getCurrentRunningService(clinicalServicesSmall);
    const activePatientId = patient?.id || patientId;
    setResumeModalState({ open: false, record: null });

    try {
      if (runningService) {
        await cancelRunningServiceStatus(runningService);
      }

      if (activePatientId) {
        navigate(`/patient/${activePatientId}/clinical-services/`);
      }
    } catch (e) {
      // Keep navigation blocked only when the cancel/update/refetch flow fails.
      // eslint-disable-next-line no-console
      console.error("Failed to cancel running service before starting a new one", e);
    }
  };

  const handleEdit = (rec, rowId) => {
    const selectRow = () => {
      setSelectedRowId(rowId);
      setSelectedSummary(rec);
      dispatch(setSelectedMedicalSummary(rec));
    };

    const getResumeDecision = () => {
      const lastUpdated = rec?.date || rec?.systemPeriod?.LowerBound || rec?.initialAssessment?.lastUpdatedAt;
      return evaluateResumeDecision(lastUpdated);
    };

    selectRow();

    if (!isRunningService(rec)) {
      dispatch(displayToast('Only services with status "In-Progress" can be edited.', 'error', { autoClose: 4000 }));
      return;
    }

    const resumeDecision = getResumeDecision();

    if (resumeDecision === "showModal") {
      setResumeModalState({ open: true, record: rec });
      return;
    }

    if (resumeDecision === "redirectDashboard") {
      if (patient?.id) navigate(`/patient/${patient.id}/clinical-services/`);
      return;
    }

    handleResumeNavigate(rec);
  };

  const handleReviewRowSelect = (item, rowId) => {
    setSelectedRowId(rowId);
    setSelectedSummary(item);
    dispatch(setSelectedMedicalSummary(item));
  };

  const renderReviewRow = (item, rowId) => (
    <MedicationReviewRow
      key={rowId}
      item={item}
      rowId={rowId}
      selectedRowId={selectedRowId}
      currentTabIndex={currentTabIndex}
      onSelect={handleReviewRowSelect}
      onEdit={handleEdit}
      onDelete={openDeleteConfirmForRecord}
      getMedicationReviewTitle={getMedicationReviewTitleByCode}
      formatStatus={formatStatus}
    />
  );

  return (
    <div className="flex flex-col pt-2">
      <div className="grid grid-cols-12 gap-3 pt-4 pb-6">
        <CustomInput
          options={medicationReviewTypeOptions}
          name="medicationReviewTypeCode"
          className="col-span-4"
          label="Medication Review Type"
          value={medicationReviewTypeCode}
          type="dropdown"
          showOptionalLabel={false}
          onChange={(e) => setMedicationReviewType(e.value)}
        />
        <CustomInput
          options={filteredDateRangeOptions}
          name="date"
          className="col-span-4"
          label="Date Range"
          value={date}
          type="dropdown"
          showOptionalLabel={false}
          onChange={(e) => setDateRange(e.value)}
        />
        <Button
          className="col-span-4 w-full !px-4 !py-3"
          text="Search"
          onClick={getMedicationReviewData}
        />
      </div>
      <div>
        {loading ? (
          <div>Loading...</div>
        ) : (
          <div className="flow-root bg-white">
            <div className="overflow-x-auto max-h-[770px] overflow-y-auto">
              <div className="inline-block min-w-full align-middle">
                <div className="overflow-hidden border border-gray-50 rounded-[4px]">
                  <table className="min-w-full divide-y divide-gray-100 overflow-x-auto overflow-y-auto">
                    <tbody className="divide-y divide-gray-50">
                      {/* Follow-up Reviews Section (temporarily hidden; keep this block for future use)
                      <tr className="text-teal-300 text-lg bg-gray-20">
                        <th className="px-6 py-3 gap-2 text-left font-semibold sm:pl-6">Follow-up Reviews</th>
                        <th className="px-6 py-3 gap-2 text-left font-semibold">Date Performed</th>
                        <th className="px-6 py-3 gap-2 text-left font-semibold">Performed By</th>
                        <th className="px-6 py-3 gap-2 text-left font-semibold">Results</th>
                        <th className="px-6 py-3 gap-2 text-left font-semibold">Documentation Forms</th>
                      </tr>
                      {followUpReviews.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="py-4 font-medium text-center text-gray-900">No Follow-up Reviews Found</td>
                        </tr>
                      ) : (
                        followUpReviews.map((item, index) => {
                          const rowId = item?.id ?? `${item?.medicationReviewTypeCode || 'type'}-${item?.date || 'nodate'}-followup-${index}`;
                          return (
                          <tr key={rowId} className={`transition-colors duration-300 group hover:bg-blue-100 ${selectedRowId === rowId ? "bg-blue-100" : ""}`}>
                            <td className="py-4 pl-6 pr-12  gap-3 text-base font-semibold text-gray-800 whitespace-pre-wrap">
                              <div className="flex items-center">
                                <div className="flex items-center">
                                  <input
                                    id={`medicationReviewRow-${rowId}`}
                                    name="medicationReviewRow"
                                    type="radio"
                                    value={rowId}
                                    checked={selectedRowId === rowId}
                                    onChange={() => {
                                      setSelectedRowId(rowId);
                                      setSelectedSummary(item);
                                      dispatch(setSelectedMedicalSummary(item));
                                    }}
                                    className="w-5 h-5 cursor-pointer text-teal-50 focus:ring-transparent"
                                  />
                                  <label
                                    htmlFor={`medicationReviewRow-${rowId}`}
                                    className="block ml-3 text-teal-600 text-body-md cursor-pointer"
                                  >
                                    <span className="font-semibold text-gray-800 leading-tight text-md-body">{getMedicationReviewTitleByCode(item.medicationReviewTypeCode)}</span>
                                  </label>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-3 gap-2 text-base font-semibold text-gray-800 whitespace-pre-wrap">
                              {item.date ? formatDateToDD_MMM_YYYY(new Date(item.date)) : "N/A"}
                            </td>
                            <td className="px-6 py-4 gap-2 text-base font-nomal text-gray-700 whitespace-pre-wrap">Vesna Spadafora</td>
                            <td className="px-6 py-4 gap-2 text-base font-normal text-gray-700 whitespace-pre-wrap"> {formatStatus(item)}</td>
                            <td className="px-6 py-4 gap-2">
                              <div className="flex gap-2">
                                <FormIcons selected={selectedRowId === rowId} medicalSummaryObject={item} currentTabIndex={currentTabIndex} rowId={rowId} />
                              </div>
                            </td>
                          </tr>
                        );})
                      )}
                      */}
                      {/* Annual Medication Reviews Section */}
                      <tr className="text-teal-300 text-lg bg-gray-20">
                        <th className="px-6 py-3 gap-2 text-left font-semibold sm:pl-6">Annual Medication Reviews</th>
                        <th className="px-6 py-3 gap-2 text-left font-semibold">Performed on</th>
                        <th className="px-6 py-3 gap-2 text-left font-semibold">Performed By</th>
                        <th className="px-6 py-3 gap-2 text-left font-semibold">Status</th>
                        <th className="px-6 py-3 gap-2 text-left font-semibold">Documentation Forms</th>
                        <th className="px-6 py-3 gap-2 text-center font-semibold">Actions</th>
                      </tr>
                      {annualReviews.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="py-4 font-medium text-center text-gray-900">No Annual Medication Reviews Found</td>
                        </tr>
                      ) : (
                        annualReviews.map((item, index) => renderReviewRow(item, getMedicationReviewRowId(item, index, 'annual')))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <ConfirmationModalNew
        title="Delete Medication Review"
        content={() => `Are you sure you want to delete this medication review?`}
        confirmAction={handleDeleteConfirm}
        open={deleteModalState.open}
        handleClose={() => setDeleteModalState({ open: false, record: null })}
        onCross={() => setDeleteModalState({ open: false, record: null })}
        confirmText="Delete"
        cancelText="Cancel"
      />
      <ResumeClinicalServiceModal
        open={resumeModalState.open}
        onStartNew={handleStartNewFromResume}
        onClose={() => setResumeModalState({ open: false, record: null })}
        resumeRecord={resumeModalState.record}
        resumeReason="timeout"
      />
    </div>
  );
};

export default MedicationReview;
